import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import NewProductReleases from '../../components/new-product-releases';
import CampaignDeals from '../../components/campaign-deals';
import CampaignBanner from './components/campaign-banner';
import ForumTopicListing from './components/forum-topic-listing';
import { PageContent } from 'src/components/page';
import FeaturedArticle from './components/featured-article';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import RecentlyVisitedProducts from '../../components/recently-visited';
import { CategoryList } from '../../components/category-list';
import AdContainer from '../../components/ads';
import SotyBanner from './components/soty-banner';
import ExpertSection from './components/experts';
import ErrorBoundary from '../../components/error-boundary';
import { plainText } from '../editorial-content/components/slices/rich-text';
import { Column } from '@prisjakt/ui/list/variants';

import {
  useSotyState,
  SotyState,
} from './components/soty-banner/use-soty-state';

import {
  LandingPageQuery,
  Campaign,
  CampaignBanner as CampaignBannerType,
  ExpertLandingPage,
  Maybe,
} from '../../types';
import { AppState } from '../../reducers';

import * as S from './styles';
import PopularFacets from './components/popular-facets';
import { CategoryWithLogo } from 'src/components/category-list/types';
import { SearchHero } from './components/search-hero';
import TrendingProducts from 'src/components/trending-products';
import FeaturedProducts from 'src/components/featured-products';
import FallbackProductRecommendations from './components/personalized-recommendations/fallback-recommendations';
import PersonalizedProductRecommendations from './components/personalized-recommendations';

type Props = {
  data: LandingPageQuery & {
    expertLandingPage: ExpertLandingPage;
  };
};

type ActiveBanners = {
  active: (CampaignBannerType & { campaign: Campaign })[];
};

const selectActiveCampaign: (state: AppState) => Campaign = state =>
  state.globals.campaigns &&
  state.globals.campaigns.active.find(
    (c: Campaign) => c.type === 'OFFER' || c.type === 'SEARCH_BANNER'
  );

const selectBigCampaignBanner: (
  state: AppState
) => CampaignBannerType = state =>
  state.globals.campaignBanners &&
  state.globals.campaignBanners.active.find(
    (banner: CampaignBannerType) => banner.type === 'LANDING_PAGE_BIG'
  );

const selectCampaignBanners: (state: AppState) => ActiveBanners = state =>
  state.globals.campaignBanners;

const LandingPage = ({ data }: Props) => {
  const { t, market } = useContext(AppContext);
  const dispatch = useDispatch();
  const activeCampaign = useSelector(selectActiveCampaign);
  const bigCampaignBanner = useSelector(selectBigCampaignBanner);
  const campaignBanners = useSelector(selectCampaignBanners);
  const sotyState = useSotyState();

  const {
    campaignDeals,
    forumTopics,
    personalizedRecommendations,
    fallbackRecommendations,
    newProductRecommendations,
    featuredProducts,
    trendingProductRecommendations,
    featuredArticle,
    rootCategories,
    popularFacets,
  } = data;

  useEffect(() => {
    dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: 'landing-page:mounted',
    });
  }, []);

  const categories: Maybe<CategoryWithLogo>[] = (rootCategories || [])
    .map(category =>
      category
        ? ({
            ...category,
            logo: { 192: category.logo },
          } as CategoryWithLogo)
        : null
    )
    .filter(x => x);

  const activeCampaignLogo =
    activeCampaign &&
    activeCampaign.banners &&
    activeCampaign.banners.find(
      banner => banner && banner.type.toUpperCase() === 'BANNER_LOGO_BIG'
    );

  const activeCampaignAsCategory: CategoryWithLogo | undefined =
    activeCampaign &&
    ({
      id: 'activeCampaign',
      name: activeCampaign.name,
      pathName: activeCampaign.web_uri && activeCampaign.web_uri.responsive,
      logo: {
        192: activeCampaignLogo ? activeCampaignLogo.imageUrl : null,
      },
    } as CategoryWithLogo);

  const beforeBanners =
    campaignBanners &&
    campaignBanners.active.filter(
      (b: CampaignBannerType) => b && b.type === 'BEFORE_PROMO_BANNER'
    );

  const categoriesWithActiveCampaign = activeCampaignAsCategory
    ? [activeCampaignAsCategory, ...categories]
    : categories;

  return (
    <S.LandingPage>
      <PageContent withAds headerHeight={0} bottomPlacements={[]}>
        <SearchHero />

        <S.Content>
          <AdContainer
            onlyWithServices={['appnexus']}
            placement="dubbelpanorama1"
            format="panorama_1"
            min={0}
            max={860}
          />
        </S.Content>

        <S.Content>
          <Column gap={1.5}>
            <AdContainer
              onlyWithServices={['publisherdesk']}
              placement="mob-banner-ad-a"
            />

            <CampaignDeals deals={campaignDeals} />

            {fallbackRecommendations && fallbackRecommendations.products && (
              <ErrorBoundary onError={() => null}>
                <FallbackProductRecommendations {...fallbackRecommendations} />
              </ErrorBoundary>
            )}

            <Panel
              containerProps={{
                'data-test': 'Panel',
                'data-test-type': 'categories',
              }}
            >
              <PanelHeader>
                <PanelTitle>{t('landing', 'categories.title')}</PanelTitle>
              </PanelHeader>
              <CategoryList
                displayLinkedCategories={false}
                categories={categoriesWithActiveCampaign}
              />
            </Panel>

            <AdContainer
              onlyWithServices={['publisherdesk']}
              placement="mob-banner-ad-b"
            />

            {bigCampaignBanner && (
              <CampaignBanner
                url={bigCampaignBanner.url}
                imageUrl={bigCampaignBanner.imageUrl}
                {...bigCampaignBanner.options}
              />
            )}

            {beforeBanners && beforeBanners.length > 0 && (
              <PromoBanner inverted banners={beforeBanners} />
            )}

            {sotyState === SotyState.PROMO && <SotyBanner />}

            {market.experts.enabled && data.expertLandingPage && (
              <Panel>
                <ExpertSection data={data.expertLandingPage} />
              </Panel>
            )}

            {data.prismicArticles &&
              data.prismicArticles.nodes.length > 0 &&
              data.prismicArticles.nodes.map((article, index) => {
                const ArticleComponent = (
                  <Panel key={article.id}>
                    <FeaturedArticle
                      pathName={article.pathName}
                      imageUrl={
                        article.data.featuredImage &&
                        article.data.featuredImage.url
                      }
                      title={plainText(article.data.title)}
                      preamble={plainText(article.data.metaDescription)}
                      linkText={t('landing', 'featured-article.read-more')}
                    />
                  </Panel>
                );

                if (index === 0) {
                  return (
                    <React.Fragment key="ad-container">
                      {ArticleComponent}
                      <AdContainer
                        placement="dubbelpanorama2"
                        format="panorama_2"
                        min={0}
                        max={860}
                        triggerPoint={100}
                      />
                    </React.Fragment>
                  );
                }

                return ArticleComponent;
              })}

            {featuredArticle && (
              <Panel>
                <FeaturedArticle
                  {...featuredArticle}
                  linkText={t('landing', 'featured-article.read-more')}
                />
              </Panel>
            )}
            <RecentlyVisitedProducts />

            {personalizedRecommendations &&
              personalizedRecommendations.products &&
              !personalizedRecommendations.products.fallbackToDefault && (
                <ErrorBoundary onError={() => null}>
                  <PersonalizedProductRecommendations
                    {...personalizedRecommendations}
                  />
                </ErrorBoundary>
              )}

            {newProductRecommendations && (
              <NewProductReleases {...newProductRecommendations} />
            )}

            {trendingProductRecommendations && (
              <TrendingProducts {...trendingProductRecommendations} />
            )}

            <AdContainer
              placement="dubbelpanorama3"
              format="panorama_3"
              min={0}
              max={860}
              spacing={{ bottom: '20px' }}
              triggerPoint={100}
            />

            {featuredProducts &&
              featuredProducts.products &&
              featuredProducts.products.length > 0 && (
                <FeaturedProducts featuredProducts={featuredProducts} />
              )}

            {forumTopics && <ForumTopicListing topics={forumTopics} />}

            {popularFacets && <PopularFacets popularFacets={popularFacets} />}
          </Column>
        </S.Content>
      </PageContent>
    </S.LandingPage>
  );
};

export default LandingPage;
